import {
    Approved,
    DecisionDataStore,
    Reviewing,
    ApplicationState,
    SecondLookState,
    Declined,
    UnderReview,
    CounterOffer,
} from '@servicetitan/financing-decision-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SubmitApplicationDecision,
    type GetLoanApiResponse,
} from '@servicetitan/financing-api-client';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import {
    WorkflowStore,
    SetupDataStore,
    WorkflowButtonsStore,
    useSecondLookIntegration,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';
import { useLoanSubmission } from './hooks/useLoanSubmission';
import { useFinancingPrequalSubmission as useFinancingPrequalSubmissionHook } from './hooks/useFinancingPrequalSubmission';
import { useSecondLookSubmission } from './hooks/useSecondLookSubmission';
import { Flex } from '@servicetitan/anvil2';
import { useLoanStatusPolling } from './hooks/useLoanStatusPolling';
import { useBreakpoint } from '@servicetitan/financing-utils-shared';
import { useLoanRefresh } from './hooks/useLoanRefresh';
import { usePrequalificationHardSubmission } from './hooks/usePrequalificationHardSubmission';
import { useGreenSkyIovation } from '@servicetitan/financing-application-shared';

const ReviewingWorkflowInner = () => {
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const [applicationState, setApplicationState] = useState<ApplicationState>('sending');
    const [secondLookState, setSecondLookState] = useState<SecondLookState>('none');
    const [turnsDecision, setTurnsDecision] = useState<SubmitApplicationDecision>(
        SubmitApplicationDecision.Submitted,
    );
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const turnsIntegration = useSecondLookIntegration();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const { DoneButton } = WorkflowButtonsStore.useStore();
    const screenSize = useBreakpoint();
    const isNarrowScreen = screenSize.isXs || screenSize.isSm;
    const isHardPrequalification = setupData?.prequalificationId != null;
    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';
    const { fingerprint, iovationComponent } = useGreenSkyIovation();
    const { useDecisionWorkflow, useFinancingPrequalSubmission } = useProviderConfig();

    const isFinancingSoftPrequal =
        useFinancingPrequalSubmission === true &&
        !setupData?.hasTransitionedToHardApp &&
        !isHardPrequalification;

    const {
        isSuccess: isLoanSubmissionSuccess,
        prequalId,
        loanId,
    } = useLoanSubmission({
        autoSubmit: !isHardPrequalification && !isFinancingSoftPrequal,
        fingerprint,
        onSuccess: () => {
            setApplicationState('reviewing');
        },
    });

    const { isSuccess: isFinancingPrequalSuccess, loanId: financingPrequalLoanId } =
        useFinancingPrequalSubmissionHook({
            autoSubmit: isFinancingSoftPrequal,
            enabled: isFinancingSoftPrequal,
            fingerprint,
            onSuccess: () => {
                setApplicationState('reviewing');
            },
        });

    const { isSuccess: isPrequalSubmissionSuccess, loanId: prequalLoanId } =
        usePrequalificationHardSubmission({
            autoSubmit: isHardPrequalification,
            prequalId: setupData?.prequalificationId ?? '',
            fingerprint,
            onSuccess: () => {
                setApplicationState('reviewing');
            },
        });

    useLoanRefresh({
        isSubmissionSuccess:
            isPrequalSubmissionSuccess || isLoanSubmissionSuccess || isFinancingPrequalSuccess,
        prequalId,
        isHardPrequalification,
        loanId: loanId ?? prequalLoanId ?? financingPrequalLoanId,
    });

    // Second look submission hook - only submitted when main decision is declined
    const {
        submitSecondLook,
        loanId: turnsLoanId,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
    } = useSecondLookSubmission({
        autoSubmit: false,
        onSuccess: () => {
            setSecondLookState('reviewing');
        },
    });

    const decisionDataRef = useRef(decisionData);
    decisionDataRef.current = decisionData;

    const handleTurnsDecisionChange = useCallback(
        (decision: SubmitApplicationDecision, loanData: GetLoanApiResponse) => {
            if (decision === SubmitApplicationDecision.Approved) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Approved,
                    loanId: turnsLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                });
                setWorkflowStage('second-look-decision');
            }
            setTurnsDecision(decision);
        },
        [turnsLoanId, setSelectedPlan, setDecisionData, setWorkflowStage],
    );

    const handleTurnsTimeout = useCallback(() => {
        setTurnsDecision(SubmitApplicationDecision.Declined);
    }, []);

    // Second look decision polling, when we have it approved, we change workflow stage to second-look-decision
    useLoanStatusPolling({
        loanId: turnsLoanId,
        onDecisionChange: handleTurnsDecisionChange,
        onTimeout: handleTurnsTimeout,
    });

    useEffect(() => {
        // For financing submissions, check loanId; for prequal submissions, check prequalId
        // If we have a decision, proceed regardless (decision might be set before ID is available)
        const hasFinancingId = !!decisionData?.loanId || !!loanId;
        const hasPrequalId = isHardPrequalification ? !!setupData?.prequalificationId : !!prequalId;
        if (!decisionData?.decision && !hasFinancingId && !hasPrequalId) {
            return;
        }

        if (decisionData?.decision === SubmitApplicationDecision.Approved) {
            setApplicationState('approved');
        } else if (decisionData?.decision === SubmitApplicationDecision.Declined) {
            setApplicationState('declined');

            if (turnsIntegration.isEnabled && tenantSettingsLoaded && !isBelowWaterfallThreshold) {
                setSecondLookState('sending');
                submitSecondLook();
            }
        } else if (decisionData?.decision === SubmitApplicationDecision.Submitted) {
            // No decision yet. Keep waiting.
        } else {
            setApplicationState('reviewing');
        }
    }, [
        decisionData?.decision,
        decisionData?.loanId,
        loanId,
        prequalId,
        isHardPrequalification,
        setupData?.prequalificationId,
        turnsIntegration.isEnabled,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        submitSecondLook,
    ]);

    // Transition to decision workflow for providers that require it (e.g., Service Finance)
    useEffect(() => {
        // Only transition if provider is configured to use decision workflow
        if (!useDecisionWorkflow) {
            return;
        }

        // Don't transition if no decision yet
        if (
            !decisionData?.decision ||
            decisionData.decision === SubmitApplicationDecision.Submitted
        ) {
            return;
        }

        // Don't transition if Turns is enabled and we're waiting for Turns decision
        // (but skip this wait if the amount is below the waterfall threshold)
        if (
            turnsIntegration.isEnabled &&
            !isBelowWaterfallThreshold &&
            decisionData.decision === SubmitApplicationDecision.Declined
        ) {
            const turnsTransientDecisions = [
                SubmitApplicationDecision.Approved,
                SubmitApplicationDecision.Review,
                SubmitApplicationDecision.Submitted,
            ];
            // Wait for Turns decision if it's still in progress
            if (turnsTransientDecisions.includes(turnsDecision) && secondLookState !== 'none') {
                return;
            }
        }

        // Transition to decision stage for approved applications
        if (decisionData.decision === SubmitApplicationDecision.Approved) {
            setWorkflowStage('decision');
        }
    }, [
        useDecisionWorkflow,
        decisionData?.decision,
        turnsIntegration.isEnabled,
        isBelowWaterfallThreshold,
        turnsDecision,
        secondLookState,
        setWorkflowStage,
    ]);

    // Check if this is a second-look approval (GreenSky declined but Turns approved)
    // This happens when the loanId matches the turnsLoanId, meaning the approval
    // came from the second-look submission rather than the original GreenSky submission
    const isSecondLookApproval =
        decisionData?.decision === SubmitApplicationDecision.Approved &&
        turnsLoanId != null &&
        decisionData?.loanId === turnsLoanId;

    const getDecisionComponent = () => {
        const turnsTransientDecisions = [
            SubmitApplicationDecision.Approved,
            SubmitApplicationDecision.Review,
            SubmitApplicationDecision.Submitted,
        ];
        if (decisionData?.decision === SubmitApplicationDecision.Approved) {
            return <Approved isSecondLookApproval={isSecondLookApproval} />;
        } else if (
            decisionData?.decision === SubmitApplicationDecision.Declined &&
            !turnsTransientDecisions.includes(turnsDecision)
        ) {
            return <Declined />;
        } else if (
            decisionData?.decision === SubmitApplicationDecision.Declined &&
            (!turnsIntegration.isEnabled || isBelowWaterfallThreshold)
        ) {
            return <Declined />;
        } else if (decisionData?.decision === SubmitApplicationDecision.Review) {
            return <UnderReview />;
        } else if (decisionData?.decision === SubmitApplicationDecision.CounterOffer) {
            return <CounterOffer />;
        }
        return <Reviewing applicationState={applicationState} secondLookState={secondLookState} />;
    };

    const isApproved = decisionData?.decision === SubmitApplicationDecision.Approved;

    return (
        <Flex
            direction="column"
            gap="6"
            alignItems="center"
            style={{ width: '100%', margin: '0 auto' }}
        >
            {isGreenSky && iovationComponent}
            <div style={{ flexGrow: 1, width: '100%' }}>{getDecisionComponent()}</div>
            {isApproved && !useDecisionWorkflow && (
                <Flex
                    justifyContent={isNarrowScreen ? 'stretch' : 'flex-end'}
                    style={{ width: '100%' }}
                >
                    <DoneButton style={isNarrowScreen ? { width: '100%' } : {}} />
                </Flex>
            )}
        </Flex>
    );
};

export const ReviewingWorkflow = () => <ReviewingWorkflowInner />;
